import React from 'react'
import { EditorState, Modifier, RichUtils } from 'draft-js'
import { Highlight } from 'components/icons'
import { selectionBlockExists } from 'components/editor/helpers/selection'
import { adaptColourToTheme, contrastText } from 'helpers/colour'
import { ResolvedTheme } from 'helpers/theme'
import { useResolvedTheme } from 'hooks/useTheme'

/**
 * The highlight palette, as picked against a white page.
 *
 * These are text highlights, so the body text sits *on top* of them. In the dark
 * theme that text is near-white, and near-white on a pastel is unreadable — so
 * the dark variants are the same hues taken down to a tint, with an explicit
 * foreground so the highlighted run stays legible whatever the surrounding text
 * colour is.
 */
const HIGHLIGHTS = {
  red: '#FFBFB5',
  yellow: '#F7CC62',
  green: '#B5DCAF',
  blue: '#D6E8FA',
  purple: '#D8C3FF'
}

export const colorStyleFn: any = (theme: ResolvedTheme = 'light') =>
  Object.fromEntries(
    Object.entries(HIGHLIGHTS).map(([name, light]) => {
      if (theme === 'light') return [name, { background: light }]
      const background = adaptColourToTheme(light, 'dark')
      return [name, { background, color: contrastText(background) }]
    })
  )

interface HighlighterColorsProps {
  editorState: EditorState
  onChange: (nextEditState: any) => void
}

export const HighlighterColors: React.FC<HighlighterColorsProps> = ({
  editorState,
  onChange
}: HighlighterColorsProps) => {
  const colorMap = colorStyleFn(useResolvedTheme())

  function toggleColor(toggledColor: string): void {
    // Bail out if the selection points at a block that no longer exists, otherwise
    // getCurrentInlineStyle() dereferences an undefined block and throws.
    if (!selectionBlockExists(editorState)) return

    const selection = editorState.getSelection()

    // Let's just allow one color at a time. Turn off all active colors.
    const nextContentState = Object.keys(colorMap).reduce((contentState, color) => {
      return Modifier.removeInlineStyle(contentState, selection, color)
    }, editorState.getCurrentContent())

    let nextEditorState = EditorState.push(editorState, nextContentState, 'change-inline-style')

    const currentStyle = editorState.getCurrentInlineStyle()

    // Unset style override for current color.
    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state: any, color: any) => {
        return RichUtils.toggleInlineStyle(state, color)
      }, nextEditorState)
    }

    // If the color is being toggled on, apply it.
    if (!currentStyle.has(toggledColor)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, toggledColor)
    }
    onChange(nextEditorState)
  }

  return (
    <div>
      <ColorControls editorState={editorState} onToggle={toggleColor} onChange={onChange} />
    </div>
  )
}

export const colorStyleMap: any = {
  red: {
    background: '#FFBFB5'
  },
  yellow: {
    background: '#F7CC62'
  },
  green: {
    background: '#B5DCAF'
  },
  blue: {
    background: '#D6E8FA'
  },
  purple: {
    background: '#D8C3FF'
  }
}

const ColorControls = (props: any) => {
  const currentStyle = selectionBlockExists(props.editorState) ? props.editorState.getCurrentInlineStyle() : null
  const colorButtons = []
  const colorMap = colorStyleFn(useResolvedTheme())

  for (const key in colorMap) {
    colorButtons.push({ name: key, background: colorMap[key].background })
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>, color: string) {
    event.preventDefault()
    props.onToggle(color)
  }

  return (
    <div className="flex py-3 px-5">
      {colorButtons.map((button: any) => (
        <div
          key={button.name}
          className="cursor-pointer rounded-full w-[22px] h-[22px] py-0.5 px-0 my-0 mx-[5px]"
          style={{
            background: button.background,
            border: currentStyle && currentStyle.has(button.name) ? '1px solid #000' : '1px solid transparent'
          }}
          onMouseDown={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleMouseDown(event, button.name)}
        />
      ))}
    </div>
  )
}

interface HighlighterIconProps {
  onClick: (event: React.MouseEvent) => void
}

export const HighlighterIcon: React.FC<HighlighterIconProps> = ({ onClick }: HighlighterIconProps) => {
  return (
    <div
      className="flex items-center hover:bg-surface-sunken transition-all rounded cursor-pointer p-[7px] mr-2.5"
      title="Highlight Text"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      <Highlight />
    </div>
  )
}
